import React, { useState, useEffect } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import styles from './LoginPage.module.css';

const LoginPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: credentialResponse.credential,
        }),
      });

      if (!response.ok) {
        throw new Error('Authentication failed');
      }

      const data = await response.json();
      
      // Store the JWT token and user data
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      console.log('Login successful. User data:', data.user);
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      setError('Login failed. Please try again.');
      console.error('Authentication error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google authentication failed. Please try again.');
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentWrapper}>
        {/* Hero Section */}
        <div className={styles.heroSection}>
          <div className={styles.featuresBadge}>
            <span className={styles.badgeIcon}>✨</span>
            Track Your Pokémon Card Pulls
          </div>
          
          <h1 className={styles.appTitle}>
            Pull Rate Tracker
          </h1>
          
          <p className={styles.appSubtitle}>
            The ultimate tool for tracking your Pokémon card opening sessions, 
            analyzing pull rates, and measuring your luck against expected odds.
          </p>
          
          <ul className={styles.featureList}>
            <li className={styles.featureItem}>
              <span className={styles.featureIcon}>📊</span>
              Track pack openings and pull statistics
            </li>
            <li className={styles.featureItem}>
              <span className={styles.featureIcon}>📈</span>
              Visualize your luck over time with graphs
            </li>
            <li className={styles.featureItem}>
              <span className={styles.featureIcon}>🎯</span>
              Compare your rates to expected odds
            </li>
            <li className={styles.featureItem}>
              <span className={styles.featureIcon}>🗂️</span>
              Organize sessions by sets and groups
            </li>
            <li className={styles.featureItem}>
              <span className={styles.featureIcon}>⚡</span>
              Fast, intuitive, and mobile-friendly
            </li>
          </ul>

          <div className={styles.statsSection}>
            <div className={styles.statItem}>
              <div className={styles.statValue}>∞</div>
              <div className={styles.statLabel}>Sessions Tracked</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statValue}>∞</div>
              <div className={styles.statLabel}>Packs Opened</div>
            </div>
            <div className={styles.statItem}>
              <div className={styles.statValue}>∞</div>
              <div className={styles.statLabel}>Cards Pulled</div>
            </div>
          </div>
        </div>

        {/* Login Card */}
        <div className={styles.loginCard}>
          <div className={styles.loginHeader}>
            <h2 className={styles.loginTitle}>Welcome Back</h2>
            <p className={styles.loginSubtitle}>
              Sign in to access your collection tracker
            </p>
          </div>
          
          {error && (
            <div className={styles.errorMessage} role="alert">
              {error}
            </div>
          )}
          
          <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
            <div className={styles.googleLoginWrapper}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                disabled={isLoading}
                theme="outline"
                size="large"
                text="continue_with"
                shape="rectangular"
              />
            </div>
          </GoogleOAuthProvider>
          
          {isLoading && (
            <div className={styles.loadingSpinner}>
              Signing you in...
            </div>
          )}

          <div className={styles.trustIndicators}>
            <p className={styles.trustText}>
              Secure authentication powered by Google
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;